import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { EmptyStateCard } from '@/components/ui/EmptyStateCard';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import DonutChart from '@/components/ui/DonutChart';
import BarChart from '@/components/ui/BarChart';
import MetricCard from '@/components/ui/MetricCard';
import PartnerCard from '@/components/ui/PartnerCard';
import TechniqueCard from '@/components/ui/TechniqueCard';
import ScheduleStatusCard from '@/components/ui/ScheduleStatusCard';
import AchievementCard from '@/components/ui/AchievementCard';
import { usePractitionerHighlights, PractitionerHighlight } from '@/features/profile/hooks/usePractitionerHighlights';
import { getTechniqueName } from '@/shared/hooks/useTechniques';
import { Plus, Users, Star, Clock, Target, Trophy, MessageCircle, UserCheck, Award, UserPlus, BarChart3 } from 'lucide-react';

const PractitionerJourneyHighlightsSection: React.FC = () => {
  const { t, currentLanguage } = useI18n();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const queryClient = useQueryClient();
  const { highlights: highlightsData, isLoading, error, refetch } = usePractitionerHighlights();

  // Force reload when user returns to Home page
  useEffect(() => {
    if (location.pathname === '/home') {
      queryClient.invalidateQueries({ queryKey: ['practitioner-highlights'] });
      refetch();
    }
  }, [location.pathname, queryClient, refetch]);

  // Calculate total number of pages based on visible cards
  const cardsPerView = 3;
  const totalCards = highlightsData ? 11 : 0; // 11 cards total
  const totalPages = Math.ceil(totalCards / cardsPerView);

  // Handle scroll to update current index
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const cardWidth = 135;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setCurrentIndex(Math.min(newIndex, totalPages - 1));
    }
  };

  // Scroll to specific page
  const scrollToPage = (pageIndex: number) => {
    if (scrollContainerRef.current) {
      const scrollLeft = pageIndex * cardWidth * cardsPerView;
      scrollContainerRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const cardWidth = 135;

  if (isLoading) {
    return (
      <div className="mb-8">
        <h2 className="text-sm font-bold text-white font-roboto mb-4">
          {t('home.journey_highlights.title')}
        </h2>
        <div className="text-gray-400">Loading highlights...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <h2 className="text-sm font-bold text-white font-roboto mb-4">
          {t('home.journey_highlights.title')}
        </h2>
        <div className="text-red-400">Error loading highlights</div>
      </div>
    );
  }

  if (!highlightsData) {
    return null;
  }

  // Create highlights array from data
  const highlights: PractitionerHighlight[] = [
    {
      id: '1',
      type: 'total_trainings',
      title: t('home.highlights.total_trainings'),
      value: (highlightsData as any)?.totalTrainings?.total || 0,
      description: `${(highlightsData as any)?.totalTrainings?.group || 0} Grupo + ${(highlightsData as any)?.totalTrainings?.private || 0} Particular`,
      icon: '🥋'
    },
    {
      id: '2',
      type: 'new_connections',
      title: t('home.highlights.new_connections'),
      value: (highlightsData as any)?.newConnections || 0,
      description: t('home.journey_highlights.this_month'),
      icon: '🤝'
    },
    {
      id: '3',
      type: 'most_frequent_partner',
      title: t('home.highlights.most_frequent_partner'),
      value: (highlightsData as any)?.mostFrequentPartner?.name || t('home.highlights.no_partners_yet'),
      description: (highlightsData as any)?.mostFrequentPartner ? `${(highlightsData as any).mostFrequentPartner.trainingCount} treinos` : t('home.highlights.add_partners_to_see'),
      icon: '👥',
      partnerInfo: (highlightsData as any)?.mostFrequentPartner
    },
    {
      id: '5',
      type: 'total_hours',
      title: t('home.highlights.total_hours'),
      value: `${Math.round((highlightsData as any)?.totalHours || 0)}h`,
      description: t('home.highlights.on_the_mat'),
      icon: '🕐'
    },
    {
      id: '6',
      type: 'most_applied_technique',
      title: t('home.highlights.most_applied_technique'),
      value: (highlightsData as any)?.mostAppliedTechnique 
        ? getTechniqueName(
            {
              id: (highlightsData as any).mostAppliedTechnique.id || 0,
              name: (highlightsData as any).mostAppliedTechnique.name,
              name_br: (highlightsData as any).mostAppliedTechnique.name_br || undefined,
              category: ''
            },
            currentLanguage
          )
        : t('home.highlights.no_techniques_yet'),
      description: (highlightsData as any)?.mostAppliedTechnique ? `${(highlightsData as any).mostAppliedTechnique.count} vezes` : t('home.highlights.link_techniques_to_see'),
      icon: '🥊',
      techniqueInfo: (highlightsData as any)?.mostAppliedTechnique ? {
        ...(highlightsData as any).mostAppliedTechnique,
        name: getTechniqueName(
          {
            id: (highlightsData as any).mostAppliedTechnique.id || 0,
            name: (highlightsData as any).mostAppliedTechnique.name,
            name_br: (highlightsData as any).mostAppliedTechnique.name_br || undefined,
            category: ''
          },
          currentLanguage
        )
      } : null
    },
    {
      id: '7',
      type: 'schedule_status',
      title: t('home.highlights.schedule_status'),
      value: `${(highlightsData as any)?.scheduleStatus?.completedPercentage || 0}%`,
      description: `${(highlightsData as any)?.scheduleStatus?.completed || 0} completados, ${(highlightsData as any)?.scheduleStatus?.missed || 0} perdidos`,
      icon: '📊',
      scheduleStatusData: {
        completed: (highlightsData as any)?.scheduleStatus?.completed || 0,
        missed: (highlightsData as any)?.scheduleStatus?.missed || 0,
        total: ((highlightsData as any)?.scheduleStatus?.completed || 0) + ((highlightsData as any)?.scheduleStatus?.missed || 0),
        completedPercentage: (highlightsData as any)?.scheduleStatus?.completedPercentage || 0,
        missedPercentage: (highlightsData as any)?.scheduleStatus?.missedPercentage || 0
      }
    },
    {
      id: '8',
      type: 'recognitions',
      title: t('home.highlights.recognitions'),
      value: (highlightsData as any)?.recognitions || 0,
      description: ((highlightsData as any)?.recognitions || 0) > 0 ? t('home.highlights.hard_work_recognized') : t('home.highlights.train_with_partners_for_recognition'),
      icon: '🏆'
    },
    {
      id: '9',
      type: 'unique_partners',
      title: t('home.highlights.partners_youve_rolled'),
      value: (highlightsData as any)?.uniquePartners || 0,
      description: ((highlightsData as any)?.uniquePartners || 0) > 0 ? t('home.highlights.more_partners_more_growth') : t('home.highlights.log_first_roll_with_partner'),
      icon: '👥'
    },
    {
      id: '10',
      type: 'interactions',
      title: t('home.highlights.interactions'),
      value: (highlightsData as any)?.interactions || 0,
      description: ((highlightsData as any)?.interactions || 0) > 0 ? t('home.highlights.great_job_connecting') : t('home.highlights.mat_ready_for_first_post'),
      icon: '💬'
    },
    {
      id: '11',
      type: 'achievement',
      title: t('home.highlights.achievement'),
      value: (highlightsData as any)?.achievement?.title || t('home.highlights.no_milestone_yet'),
      description: (highlightsData as any)?.achievement?.description || t('home.highlights.complete_trainings_to_unlock'),
      icon: (highlightsData as any)?.achievement?.emoji || '🎯',
      achievementInfo: (highlightsData as any)?.achievement
    }
  ];

  const renderCard = (highlight: PractitionerHighlight) => {
    // For achievement type, check if achievementInfo exists
    const isEmpty = highlight.type === 'achievement' 
      ? !highlight.achievementInfo
      : highlight.value === 0 || highlight.value === '0' || highlight.value === '0.0' || highlight.value === 'No partners yet' || highlight.value === 'No techniques yet' || highlight.value === 'No milestone yet' || highlight.value === t('home.highlights.no_milestone_yet');

    return (
        <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-3 min-w-[120px] h-[180px] flex flex-col overflow-hidden">
        {/* Title */}
        {highlight.type !== 'achievement' && (
          <div className="mb-2 flex-shrink-0">
            <p className="text-[12px] font-semibold text-white font-roboto mb-1 leading-tight truncate" title={highlight.title}>
              {highlight.title}
            </p>
            <p className="text-[10px] font-light text-gray-400 font-roboto leading-tight">
              {t('home.journey_highlights.this_month')}
            </p>
          </div>
        )}

        {/* Content based on card type */}
        <div className="flex-1 flex flex-col justify-center min-h-0 overflow-hidden">
          {highlight.type === 'total_trainings' ? (
            <div className="flex flex-col items-center">
              {/* Custom Donut Chart */}
              <div className="relative" style={{ width: 64, height: 64 }}>
                <svg 
                  width={64} 
                  height={64} 
                  className="transform -rotate-90"
                  viewBox="0 0 64 64"
                >
                  {/* Background circle */}
                  <circle
                    cx={32}
                    cy={32}
                    r={28}
                    fill="none"
                    stroke="#524545"
                    strokeWidth={8}
                  />
                  
                  {/* Segments */}
                  {(() => {
                    const segments = [
                      {
                        label: t('home.highlights.group'),
                        value: (highlightsData as any)?.totalTrainings?.group || 0,
                        color: '#9852DB',
                        percentage: (highlightsData as any)?.totalTrainings?.total > 0 
                          ? ((highlightsData as any).totalTrainings.group / (highlightsData as any).totalTrainings.total) * 100 
                          : 0
                      },
                      {
                        label: t('home.highlights.private'),
                        value: (highlightsData as any)?.totalTrainings?.private || 0,
                        color: '#416FF0',
                        percentage: (highlightsData as any)?.totalTrainings?.total > 0 
                          ? ((highlightsData as any).totalTrainings.private / (highlightsData as any).totalTrainings.total) * 100 
                          : 0
                      }
                    ];
                    
                    const circumference = 2 * Math.PI * 28;
                    let currentOffset = 0;
                    
                    return segments.map((segment, index) => {
                      const segmentLength = (segment.percentage / 100) * circumference;
                      const segmentOffset = currentOffset;
                      
                      currentOffset += segmentLength;
                      
                      return (
                        <circle
                          key={index}
                          cx={32}
                          cy={32}
                          r={28}
                          fill="none"
                          stroke={segment.color}
                          strokeWidth={8}
                          strokeDasharray={`${segmentLength} ${circumference}`}
                          strokeDashoffset={-segmentOffset}
                          strokeLinecap="round"
                        />
                      );
                    });
                  })()}
                </svg>
              </div>
              
              {/* Legend with category names and values */}
              <div className="flex justify-center space-x-4 mt-3">
                {(() => {
                  const segments = [
                    {
                      label: t('home.highlights.group'),
                      value: (highlightsData as any)?.totalTrainings?.group || 0,
                      color: '#9852DB',
                      percentage: (highlightsData as any)?.totalTrainings?.total > 0 
                        ? ((highlightsData as any).totalTrainings.group / (highlightsData as any).totalTrainings.total) * 100 
                        : 0
                    },
                    {
                      label: t('home.highlights.private'),
                      value: (highlightsData as any)?.totalTrainings?.private || 0,
                      color: '#416FF0',
                      percentage: (highlightsData as any)?.totalTrainings?.total > 0 
                        ? ((highlightsData as any).totalTrainings.private / (highlightsData as any).totalTrainings.total) * 100 
                        : 0
                    }
                  ];
                  
                  return segments.map((segment, index) => (
                    <div key={index} className="flex items-center space-x-1">
                      <div 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: segment.color }}
                      />
                      <span className="text-[8px] text-white whitespace-nowrap">
                        {segment.label} ({segment.value})
                      </span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          ) : highlight.type === 'new_connections' ? (
            <MetricCard
              value={highlight.value}
              description={isEmpty ? t('home.highlights.connections_empty_message') : t('home.journey_highlights.connections_message', { count: (highlightsData as any)?.newConnections || 0, month: 'mês' })}
              isEmpty={isEmpty}
            />
        ) : highlight.type === 'most_frequent_partner' ? (
          <PartnerCard
            partner={highlight.partnerInfo}
            isEmpty={isEmpty}
            emptyMessage={t('home.highlights.partners_empty_message')}
          />
        ) : highlight.type === 'total_hours' ? (
          <MetricCard
            value={highlight.value}
            icon="🕐"
            description={isEmpty ? t('home.highlights.hours_empty_message') : t('home.highlights.hours_message')}
            isEmpty={isEmpty}
          />
        ) : highlight.type === 'most_applied_technique' ? (
          <TechniqueCard
            technique={highlight.techniqueInfo}
            isEmpty={isEmpty}
            emptyMessage={t('home.highlights.technique_empty_message')}
          />
        ) : highlight.type === 'schedule_status' ? (
          <ScheduleStatusCard
            data={highlight.scheduleStatusData}
            isEmpty={isEmpty}
          />
        ) : highlight.type === 'achievement' ? (
          <AchievementCard
            achievement={highlight.achievementInfo}
            isEmpty={isEmpty}
          />
        ) : (
          <MetricCard
            value={highlight.value}
            icon={highlight.icon}
            description={highlight.description}
            isEmpty={isEmpty}
          />
        )}
        </div>
      </div>
    );
  };

  return (
    <div className="mb-8">
      <h2 className="text-sm font-bold text-white font-roboto mb-4">
        {t('home.journey_highlights.title')}
      </h2>
      
      <div className="relative overflow-hidden" style={{ maxWidth: 'calc(100vw - 20px)' }}>
        <div 
          className="w-full overflow-x-auto pb-2 scrollbar-hide" 
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          <div className="flex space-x-4">
            {highlights.map((highlight) => (
              <div key={highlight.id}>
                {renderCard(highlight)}
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-4 space-x-2">
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index}
                onClick={() => scrollToPage(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-white' : 'bg-gray-500'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PractitionerJourneyHighlightsSection;
